<template>
  <div class="mt-6 space-y-4">
    <button
      @click="
        exportDiagramAsPDF();
        trackExport('cutting_diagram_pdf');
      "
      class="bg-green-500 hover:bg-green-600 text-white font-semibold py-2 px-6 rounded-md transition duration-200"
      v-if="diagramReady"
    >
      Export Cutting Diagram as PDF
    </button>

    <p v-if="exportMessage" class="text-sm italic text-gray-600">
      {{ exportMessage }}
    </p>
  </div>
</template>

<script setup>
  import { ref, onMounted } from 'vue';
  // We'll use dynamic import to avoid SSR issues with jsPDF
  let jsPDF;

  // Initialize analytics tracking
  const { trackExport } = useAnalytics();
  onMounted(async () => {
    // Import jsPDF dynamically only on the client side
    const jspdfModule = await import('jspdf');
    jsPDF = jspdfModule.jsPDF;
  });

  const props = defineProps({
    diagramElement: {
      type: Object,
      default: null,
    },
    diagramReady: {
      type: Boolean,
      default: false,
    },
    calculationData: {
      type: Object,
      default: () => ({
        stockLength: 6,
        requiredPieces: [
          { length: 33, quantity: 10 },
          { length: 25, quantity: 12 },
        ],
        barsNeeded: 2,
        wasteLength: 0,
        efficiency: 0,
        cuttingPattern: [],
        kerfWidth: 3, // Default kerf width in mm
        summaryPieces: [],
      }),
    },
  });

  const exportMessage = ref('');

  // Function to export the cutting diagram as PDF
  async function exportDiagramAsPDF() {
    if (!props.calculationData) {
      exportMessage.value = 'No calculation data available';
      return;
    }

    try {
      exportMessage.value = 'Generating PDF...';

      // Define colors for piece types (same as in CuttingOptimizer.vue)
      const pieceColors = [
        [96, 165, 250], // Blue
        [52, 211, 153], // Green
        [167, 139, 250], // Purple
        [249, 115, 22], // Orange
        [236, 72, 153], // Pink
        [20, 184, 166], // Teal
        [139, 92, 246], // Indigo
        [245, 158, 11], // Amber
        [6, 182, 212], // Cyan
      ];

      // If jsPDF is not loaded yet, load it now
      if (!jsPDF) {
        const jspdfModule = await import('jspdf');
        jsPDF = jspdfModule.jsPDF;
      }

      // Create PDF document (A4 size)
      const pdf = new jsPDF({
        orientation: 'landscape',
        unit: 'mm',
        format: 'a4',
      });

      // Add title
      pdf.setFontSize(20);
      pdf.text('Steel Bar Cutting Diagram', 15, 15);

      // Helper function to safely get values from refs or regular values
      const getValue = (obj) => {
        return typeof obj === 'object' && obj !== null && 'value' in obj
          ? obj.value
          : obj;
      };

      // Get actual values for PDF text
      const stockLengthVal = getValue(props.calculationData.stockLength);
      const pieceLengthVal = getValue(props.calculationData.pieceLength);
      const piecesNeededVal = getValue(props.calculationData.piecesNeeded);
      const barsNeededVal = getValue(props.calculationData.barsNeeded);
      const wasteLengthVal = getValue(props.calculationData.wasteLength);
      const efficiencyVal = getValue(props.calculationData.efficiency);
      const kerfWidthValue = getValue(props.calculationData.kerfWidth);

      // Add information about the calculation
      pdf.setFontSize(12);

      // Create the required pieces text
      const requiredPiecesVal =
        getValue(props.calculationData.requiredPieces) || [];
      const summaryPiecesVal =
        getValue(props.calculationData.summaryPieces) || [];

      // Create pieces summary text
      let piecesText = 'Required Pieces: ';
      if (summaryPiecesVal && summaryPiecesVal.length > 0) {
        piecesText += summaryPiecesVal
          .map((piece) => `${piece.quantity} x ${piece.length}cm`)
          .join(', ');
      } else if (requiredPiecesVal && requiredPiecesVal.length > 0) {
        piecesText += requiredPiecesVal
          .map((piece) => `${piece.quantity} x ${piece.length}cm`)
          .join(', ');
      } else {
        piecesText += 'N/A';
      }

      pdf.text(
        [
          `Stock Length: ${stockLengthVal} m, Kerf : ${kerfWidthValue} mm`,
          piecesText,

          `Total Bars Needed: ${barsNeededVal}`,
          `Total Waste: ${wasteLengthVal.toFixed(2)} cm (${(
            wasteLengthVal / 100
          ).toFixed(2)} m)`,
          `Efficiency: ${efficiencyVal.toFixed(2)}%`,
        ],
        15,
        25
      );

      // Create visualization manually
      const startY = 50;
      let currentY = startY;

      // Draw the cutting pattern
      const cuttingPatternVal = getValue(props.calculationData.cuttingPattern);
      console.log('Cutting Pattern in PDF:', cuttingPatternVal);

      if (cuttingPatternVal && cuttingPatternVal.length > 0) {
        cuttingPatternVal.forEach((bar, barIndex) => {
          // Bar label
          pdf.text(`Bar ${barIndex + 1}:`, 15, currentY);
          currentY += 5;

          // Bar visualization
          const barStartX = 15;
          const barWidth = 260; // width in mm on the PDF
          const barHeight = 15;

          // Draw bar outline
          pdf.rect(barStartX, currentY, barWidth, barHeight);

          // Draw pieces within the bar
          let currentX = barStartX;
          // Handle both ref and non-ref values
          const stockLengthValue =
            typeof props.calculationData.stockLength === 'object' &&
            props.calculationData.stockLength.value !== undefined
              ? props.calculationData.stockLength.value
              : props.calculationData.stockLength;
          const totalBarLength = stockLengthValue * 100; // in cm      // Get kerf width for drawing cut lines
          const kerfWidthValue = getValue(props.calculationData.kerfWidth);
          const kerfWidthCm = kerfWidthValue / 10; // Convert mm to cm
          const kerfWidthPDF = (kerfWidthCm / totalBarLength) * barWidth; // Convert to PDF units

          bar.pieces.forEach((piece, pieceIndex) => {
            const pieceWidth = (piece.length / totalBarLength) * barWidth;

            // Fill color based on waste/piece
            if (piece.isWaste) {
              // Light red for waste
              pdf.setFillColor(248, 113, 113);
            } else {
              // Color based on piece type
              const pieceTypeIndex = piece.pieceTypeIndex || 0;
              const color = pieceColors[pieceTypeIndex % pieceColors.length];
              pdf.setFillColor(color[0], color[1], color[2]);
            }

            // Draw and fill the piece rectangle
            pdf.rect(currentX, currentY, pieceWidth, barHeight, 'F');

            // Add piece length label
            pdf.setTextColor(0, 0, 0);
            let pieceLabel = `${piece.length.toFixed(1)}cm`;
            if (!piece.isWaste && piece.pieceTypeIndex !== undefined) {
              pieceLabel += ` (T${piece.pieceTypeIndex + 1})`;
            }

            // Only add text if piece is wide enough
            if (pieceWidth > 20) {
              pdf.text(
                pieceLabel,
                currentX + pieceWidth / 2 - 7,
                currentY + barHeight / 2 + 1
              );
            }

            // Draw vertical cut line after each non-waste piece except the last one
            if (!piece.isWaste && pieceIndex < bar.pieces.length - 1) {
              // Set line width based on kerf width
              pdf.setDrawColor(0); // Black
              pdf.setFillColor(50, 50, 50); // Dark gray

              // Draw a filled rectangle to represent the kerf
              pdf.rect(
                currentX + pieceWidth, // X position at the end of the piece
                currentY - 2, // Start slightly above the bar
                kerfWidthPDF, // Width based on kerf width
                barHeight + 4, // Extend slightly beyond bar height
                'F' // Fill
              );
            }

            // Move to next piece
            currentX += pieceWidth;
          });

          // Move to next bar
          currentY += barHeight + 10;
        });

        // Add piece type legend at the bottom
        currentY += 10;
        pdf.text('Piece Type Legend:', 15, currentY);
        currentY += 5;

        // Get required pieces for the legend
        const requiredPieces =
          getValue(props.calculationData.requiredPieces) || [];
        if (requiredPieces && requiredPieces.length > 0) {
          requiredPieces.forEach((piece, index) => {
            const color = pieceColors[index % pieceColors.length];

            // Draw color square
            pdf.setFillColor(color[0], color[1], color[2]);
            pdf.rect(15, currentY - 4, 5, 5, 'F');

            // Add piece description
            pdf.text(
              `Type ${index + 1}: ${piece.length}cm x ${piece.quantity} pieces`,
              25,
              currentY
            );

            currentY += 6;
          });
        }
      }

      // Open the PDF in a new browser tab instead of downloading
      const pdfOutput = pdf.output('bloburl');
      window.open(pdfOutput, '_blank');

      exportMessage.value = 'PDF opened in a new tab!';

      // Clear message after 3 seconds
      setTimeout(() => {
        exportMessage.value = '';
      }, 3000);
    } catch (error) {
      console.error('Error exporting PDF:', error);
      exportMessage.value = 'Error exporting PDF. Please try again.';
    }
  }
</script>
